import TenantComponents from '@components';
import tenantConstants from '@constants';
import { Radio, Typography } from 'antd';
import cx from 'clsx';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Alert, Card, ConfirmationModal, Flex, Icon } from '../../../../components/common';
import { CardCompact } from '../../../../components/common/cards/styled';
import { IconGoldenPinMap } from '../../../../components/widgets/GoldenPinSvg';
import { formatPrice } from '../../../../utility/utility';
import { RadioStyled } from '../../../common/components/listing/listing-platform-actions/styled';
import { GoldenPinDialogGlobalStyle, HeroFrame } from './styled';

const { Text } = Typography;

/**
 * Apply Golden Pin modal (web) / sheet (mweb). Same chrome as the other upgrade modals
 * (Request Signature Upgrade etc.: ConfirmationModal header, payment cards, footer buttons), with the
 * map hero and the golden pin description on top. Exposes the same imperative API as QuotaCreditModal
 * so useApplyProductModalData can drive it unchanged. Price and duration come from `action`
 * (applicable_products), never hard-coded.
 */
const GoldenPinApplyModal = forwardRef(
  (
    { action, onSuccess, onCancel, loading, paymentOptions = [], selectedPaymentOption, onChangePaymentOption },
    ref,
  ) => {
    const { t } = useTranslation();
    const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
    const modalRef = useRef();

    useImperativeHandle(ref, () => ({
      showQuotaCreditModal: () => modalRef.current?.showModal(),
      hideQuotaCreditModal: () => modalRef.current?.hideModal(),
      setLoading: (value) => modalRef.current?.setLoading(value),
    }));

    const insufficient = action?.isSufficient === false && selectedPaymentOption === 'credits';

    return (
      <>
        <GoldenPinDialogGlobalStyle />
        <ConfirmationModal
          ref={modalRef}
          title={t('Apply Golden Pin')}
          width="708px"
          okText={insufficient ? 'Continue' : 'Submit'}
          onSuccess={onSuccess}
          onCancel={onCancel}
          loading={loading}
          className="golden-pin-apply"
          bodyStyle={isMobile ? undefined : { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' }}
          destroyOnClose
        >
          <HeroFrame className="mb-16">
            <IconGoldenPinMap
              variant={isMobile ? 'mweb' : 'web'}
              shimmer
              fit
              title={t('A gold pin standing out among ordinary price pins on the Bayut map')}
            />
          </HeroFrame>
          <p className="mb-16">
            <Trans
              i18nKey="goldenPinApplyDescription"
              values={{ credits: action?.requiredCredits, days: action?.expiryDays }}
              components={{ b: <strong /> }}
            />
          </p>

          <Flex justify="space-between" align="center" className="mb-8" wrap="wrap" gap="8px">
            <Text className="fw-700">{t('Payment Method')}</Text>
            {action?.available_credits != null && (
              <Text className="fw-700" style={{ marginInlineStart: 'auto' }}>
                <span className="text-muted fw-400 fz-12">{t('goldenPinAvailableCredits')} </span>
                {formatPrice(action?.available_credits)}
              </Text>
            )}
          </Flex>
          <Radio.Group value={selectedPaymentOption} className="w-100">
            {paymentOptions.map((option) => (
              <Card
                key={option.key}
                as={selectedPaymentOption == option.key ? CardCompact : undefined}
                compactCardRadius="8px"
                compactCardPadding="12px"
                onClick={() => onChangePaymentOption?.(option)}
                style={{ borderWidth: 1 }}
                className={cx('mb-12', 'pointer')}
              >
                <Flex justify="space-between" align="center">
                  <RadioStyled value={option.key} className="radioInput">
                    <TenantComponents.Product
                      title={option.key === 'credits' ? t('goldenPinCredits') : option.title}
                      description={option.description}
                    />
                  </RadioStyled>
                  <Flex align="center" gap="4px">
                    {/* prefix is the credit coin icon for Credits and the currency symbol for card */}
                    <div className="fz-12 text-primary text-muted" style={{ display: 'inline-flex' }}>
                      {option.prefix}
                    </div>
                    <div className="fw-700 fz-16 text-primary">{formatPrice(option.value)}</div>
                  </Flex>
                </Flex>
              </Card>
            ))}
          </Radio.Group>
          {insufficient && (
            <Alert
              style={{ padding: 0 }}
              showIcon
              type=""
              icon={<Icon className="color-gray-dark" icon="HiInformationCircle" size={16} />}
              message={
                <div
                  className={cx('color-gray-light', isMobile ? 'fz-12' : 'fz-14')}
                  style={{ paddingBlockStart: !isMobile && '3px', lineHeight: isMobile && 1.5 }}
                >
                  {t('Insufficient Credits')}
                  {'.'} {t('Pay')}{' '}
                  <strong className="color-primary">
                    {tenantConstants.CURRENCY_SYMBOL()}{' '}
                    {formatPrice(Math.round(action?.creditsDiff * action?.credit_unit_price))}
                  </strong>{' '}
                  {t(`for the additional {{credits}} credits at checkout`, { credits: action?.creditsDiff })}
                </div>
              }
            />
          )}
        </ConfirmationModal>
      </>
    );
  },
);

export default GoldenPinApplyModal;
